import type { FinancialBasis, AssessmentStatus, AssessmentReason } from "./financial-availability.js";
import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { deals, facilities } from "./schema-deals.js";
import { lenders } from "./schema-lenders.js";
import { contacts, accounts } from "./schema-crm.js";

// ============================================================================
// FINANCIAL MODULE (Deal Serviceability, ICR, DSR, Income, Expenses, etc.)
// ============================================================================

// NOTE: Using existing database enum fa_serviceability_approach
export const faServiceabilityApproachEnum = pgEnum("fa_serviceability_approach", [
  "customer_rate",
  "assessment_rate",
  "customer_rate_plus_buffer",
  "higher_of"
]);

export const dealServiceability = pgTable("crm_x_deal_serviceability", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),

  // Rate Configuration
  serviceabilityApproach: faServiceabilityApproachEnum("serviceability_approach").default("higher_of").notNull(),
  customerRate: numeric("customer_rate", { precision: 15, scale: 5 }).notNull(), // Store as decimal (0.065 for 6.5%)
  assessmentRate: numeric("assessment_rate", { precision: 15, scale: 5 }),
  bufferRate: numeric("buffer_rate", { precision: 15, scale: 5 }),
  stressBps: integer("stress_bps").notNull().default(100),
  rateUsed: numeric("rate_used", { precision: 15, scale: 5 }),

  // Loan Details
  loanAmount: numeric("loan_amount", { precision: 15, scale: 2 }).notNull(),
  loanTermMonths: integer("loan_term_months").notNull(),
  repaymentType: text("repayment_type").default("principal_and_interest"), // text, not enum

  // Calculated Repayments
  serviceabilityRepaymentAnnual: numeric("serviceability_repayment_annual", { precision: 15, scale: 2 }),
  serviceabilityRepaymentMonthly: numeric("serviceability_repayment_monthly", { precision: 15, scale: 2 }),

  // Income Configuration
  incomeSource: text("income_source"), // text, not enum
  totalAvailableIncome: numeric("total_available_income", { precision: 15, scale: 2 }),
  incomeMethodology: text("income_methodology"),

  // Commitments
  totalExistingCommitments: numeric("total_existing_commitments", { precision: 15, scale: 2 }),
  proposedCommitment: numeric("proposed_commitment", { precision: 15, scale: 2 }),
  totalCommitments: numeric("total_commitments", { precision: 15, scale: 2 }),

  // Calculated Ratios
  debtServiceCoverageRatio: numeric("debt_service_coverage_ratio", { precision: 10, scale: 4 }),
  interestCoverageRatio: numeric("interest_coverage_ratio", { precision: 10, scale: 4 }),

  // Lender Requirements
  lenderMinDscr: numeric("lender_min_dscr", { precision: 5, scale: 2 }),
  lenderMinIcr: numeric("lender_min_icr", { precision: 5, scale: 2 }),

  // Pass/Fail Results (stored as integer: 0/1)
  passesDscr: integer("passes_dscr"),
  passesIcr: integer("passes_icr"),
  passesServicing: integer("passes_servicing"),

  // Notes
  servicingNotes: text("servicing_notes"),

  // Advanced Analysis
  sensitivityAnalysis: jsonb("sensitivity_analysis"),
  guarantorStatements: jsonb("guarantor_statements"),
  guarantorIncomeContribution: numeric("guarantor_income_contribution", { precision: 15, scale: 2 }).default("0"),
  guarantorDebtContribution: numeric("guarantor_debt_contribution", { precision: 15, scale: 2 }).default("0"),

  // Audit fields
  organisationId: uuid("organisation_id"),
  calculatedAt: timestamp("calculated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 8c. Income Sources Table (SPRINT-6B F004)
// Income source configuration and shading overrides for deal serviceability calculations
export const dealIncomeSources = pgTable("crm_x_deal_income_sources", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),

  // Toggle fields for 6 income types
  includeBusinessIncome: boolean("include_business_income").default(false),
  includeRentalIncome: boolean("include_rental_income").default(false),
  includePersonalIncome: boolean("include_personal_income").default(false),
  includeDividendIncome: boolean("include_dividend_income").default(false),
  includeTrustIncome: boolean("include_trust_income").default(false),
  includeOtherIncome: boolean("include_other_income").default(false),

  // Shading override fields (nullable - null means use default)
  businessIncomeShading: numeric("business_income_shading", { precision: 5, scale: 2 }),
  rentalIncomeShading: numeric("rental_income_shading", { precision: 5, scale: 2 }),
  personalIncomeShading: numeric("personal_income_shading", { precision: 5, scale: 2 }),
  dividendIncomeShading: numeric("dividend_income_shading", { precision: 5, scale: 2 }),
  trustIncomeShading: numeric("trust_income_shading", { precision: 5, scale: 2 }),

  // Calculated totals (updated by API)
  totalGrossIncome: numeric("total_gross_income", { precision: 15, scale: 2 }).default("0"),
  totalShadedIncome: numeric("total_shaded_income", { precision: 15, scale: 2 }).default("0"),

  // Audit fields
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// 8d. Contact Income Table (SPRINT-6B F005)
// Individual contact income entries for deal serviceability analysis
export const dealContactIncome = pgTable("crm_x_deal_contact_income", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),

  incomeType: text("income_type").notNull(), // payg_salary, payg_wage, self_employed, rental, dividend, trust_distribution, government_benefit, superannuation, investment, other
  employerSource: text("employer_source"),
  grossAmount: numeric("gross_amount", { precision: 15, scale: 2 }).notNull(),
  frequency: text("frequency").notNull(), // weekly, fortnightly, monthly, quarterly, annually

  // Auto-calculated fields
  annualGross: numeric("annual_gross", { precision: 15, scale: 2 }).notNull().default("0"),
  shadingPercentage: numeric("shading_percentage", { precision: 5, scale: 2 }).notNull().default("100.00"),
  shadedAnnual: numeric("shaded_annual", { precision: 15, scale: 2 }).notNull().default("0"),

  includeInServicing: boolean("include_in_servicing").default(true),
  verificationStatus: text("verification_status").default("unverified"), // unverified, pending, verified, rejected
  verificationDate: date("verification_date"),
  evidenceNotes: text("evidence_notes"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 8e. ICR Calculations Table (SPRINT-6B F002)
// Interest Coverage Ratio calculations for deal financial analysis
export const icrStatusEnum = pgEnum("icr_status_enum", ["strong", "adequate", "marginal", "weak"]);
export const trendDirectionEnum = pgEnum("trend_direction_enum", ["improving", "stable", "declining"]);

export const icrCalculations = pgTable("crm_x_icr_calculations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  facilityId: uuid("facility_id").references(() => facilities.id, { onDelete: "set null" }),
  financialPeriodId: uuid("financial_period_id"),

  // Period Info
  calculationDate: date("calculation_date").notNull(),
  periodLabel: text("period_label").notNull(),

  // Income Components
  ebitda: numeric("ebitda", { precision: 15, scale: 2 }).notNull(),
  adjustedEbitda: numeric("adjusted_ebitda", { precision: 15, scale: 2 }).notNull(),
  addbacks: numeric("addbacks", { precision: 15, scale: 2 }),
  totalIncomeForServicing: numeric("total_income_for_servicing", { precision: 15, scale: 2 }).notNull(),

  // Interest Components
  existingInterest: numeric("existing_interest", { precision: 15, scale: 2 }).notNull(),
  proposedInterest: numeric("proposed_interest", { precision: 15, scale: 2 }).notNull(),
  totalInterest: numeric("total_interest", { precision: 15, scale: 2 }).notNull(),

  // Calculated Ratios
  icrRatio: numeric("icr_ratio", { precision: 10, scale: 2 }).notNull(),
  icrStatus: icrStatusEnum("icr_status").notNull(),

  // Lender Requirements
  lenderMinIcr: numeric("lender_min_icr", { precision: 10, scale: 2 }),
  meetsRequirement: integer("meets_requirement"),
  headroomPercentage: numeric("headroom_percentage", { precision: 10, scale: 2 }),

  // Trend Analysis
  priorPeriodIcr: numeric("prior_period_icr", { precision: 10, scale: 2 }),
  priorPeriod2Icr: numeric("prior_period_2_icr", { precision: 10, scale: 2 }),
  priorPeriod3Icr: numeric("prior_period_3_icr", { precision: 10, scale: 2 }),
  yoyChange: numeric("yoy_change", { precision: 10, scale: 2 }),
  yoyChangePercentage: numeric("yoy_change_percentage", { precision: 10, scale: 2 }),
  trendDirection: trendDirectionEnum("trend_direction"),
  trendPeriodsAnalysed: integer("trend_periods_analysed"),
  threeYearAvgIcr: numeric("three_year_avg_icr", { precision: 10, scale: 2 }),
  threeYearMinIcr: numeric("three_year_min_icr", { precision: 10, scale: 2 }),
  threeYearMaxIcr: numeric("three_year_max_icr", { precision: 10, scale: 2 }),

  // Analysis
  sensitivityAnalysis: jsonb("sensitivity_analysis"),

  // Notes
  calculationNotes: text("calculation_notes"),

  // Audit fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 8e. Account Commitments Table (SPRINT-6B F006)
// Account-level debt commitments with auto-annualisation
export const accountCommitments = pgTable("crm_x_account_commitments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }).notNull(),

  commitmentType: text("commitment_type").notNull(), // loan, lease, hire_purchase, chattel_mortgage, overdraft, credit_card, other
  lenderCreditor: text("lender_creditor"),
  lenderId: uuid("lender_id"),
  description: text("description"),
  originalAmount: numeric("original_amount", { precision: 15, scale: 2 }),
  currentBalance: numeric("current_balance", { precision: 15, scale: 2 }).notNull(),
  creditLimit: numeric("credit_limit", { precision: 15, scale: 2 }),
  repaymentFrequency: text("repayment_frequency").notNull(), // weekly, fortnightly, monthly, quarterly, annually
  repaymentAmount: numeric("repayment_amount", { precision: 15, scale: 2 }).notNull(),
  annualRepayment: numeric("annual_repayment", { precision: 15, scale: 2 }).notNull().default("0"),
  interestRate: numeric("interest_rate", { precision: 5, scale: 2 }),
  maturityDate: date("maturity_date"),
  includeInServicing: boolean("include_in_servicing").default(true),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 8f. Living Expenses Table (SPRINT-6B F007)
// Contact-level living expenses for serviceability analysis
export const livingExpensesTable = pgTable("crm_x_living_expenses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  contactId: uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),

  expenseCategory: text("expense_category").notNull(), // housing, utilities, transport, food_groceries, insurance, education_childcare, telecommunications, health_medical, personal_lifestyle, child_support_alimony, other
  description: text("description"),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull(),
  frequency: text("frequency").notNull(), // weekly, fortnightly, monthly, quarterly, annually
  annualAmount: numeric("annual_amount", { precision: 15, scale: 2 }).notNull().default("0"),

  isJointExpense: boolean("is_joint_expense").default(false),
  hemApplicable: boolean("hem_applicable").default(true),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 8g. DSR Calculations Table (SPRINT-6B F003)
// Debt Service Ratio calculations for deal financial analysis
export const dsrStatusEnum = pgEnum("dsr_status_enum", ["strong", "adequate", "marginal", "weak"]);

export const dsrCalculations = pgTable("crm_x_dsr_calculations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  facilityId: uuid("facility_id").references(() => facilities.id, { onDelete: "set null" }),
  financialPeriodId: uuid("financial_period_id"),

  // Period Info
  calculationDate: date("calculation_date").notNull(),
  periodLabel: text("period_label").notNull(),

  // Income Components
  grossIncome: numeric("gross_income", { precision: 15, scale: 2 }).notNull().default("0"),
  incomeShadingApplied: numeric("income_shading_applied", { precision: 5, scale: 2 }).notNull().default("100"), // Percentage
  assessableIncome: numeric("assessable_income", { precision: 15, scale: 2 }).notNull().default("0"),

  // Expense Components
  livingExpenses: numeric("living_expenses", { precision: 15, scale: 2 }).notNull().default("0"),
  hemBenchmark: numeric("hem_benchmark", { precision: 15, scale: 2 }),
  operatingExpenses: numeric("operating_expenses", { precision: 15, scale: 2 }).notNull().default("0"),
  otherCommitments: numeric("other_commitments", { precision: 15, scale: 2 }).notNull().default("0"),

  // Debt Service Components
  existingDebtService: numeric("existing_debt_service", { precision: 15, scale: 2 }).notNull().default("0"),
  proposedDebtService: numeric("proposed_debt_service", { precision: 15, scale: 2 }).notNull().default("0"),
  totalDebtService: numeric("total_debt_service", { precision: 15, scale: 2 }).notNull().default("0"),

  // Calculated Ratios
  surplusIncome: numeric("surplus_income", { precision: 15, scale: 2 }).notNull().default("0"),
  dsrPercentage: numeric("dsr_percentage", { precision: 10, scale: 2 }).notNull().default("0"),
  dscrRatio: numeric("dscr_ratio", { precision: 10, scale: 2 }).notNull().default("0"),
  dsrStatus: dsrStatusEnum("dsr_status").notNull(),

  // Lender Requirements
  lenderMaxDsr: numeric("lender_max_dsr", { precision: 10, scale: 2 }),
  lenderMinDscr: numeric("lender_min_dscr", { precision: 10, scale: 2 }),
  meetsRequirement: integer("meets_requirement"),

  // Trend Analysis
  trendDirection: trendDirectionEnum("trend_direction"),

  // Notes
  calculationNotes: text("calculation_notes"),

  // Audit fields
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// 8h. Deal Adjustments Table (SPRINT-6B F008)
// EBITDA normalisation adjustments at the deal level
export const dealAdjustments = pgTable("crm_x_deal_adjustments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  accountId: uuid("account_id"),
  financialPeriodId: uuid("financial_period_id"),

  adjustmentType: text("adjustment_type").notNull(),
  description: text("description").notNull(),
  amount: numeric("amount", { precision: 15, scale: 2 }).notNull(),
  adjustmentDirection: integer("adjustment_direction").notNull(), // 1 for addback, -1 for deduction
  ebitdaImpact: numeric("ebitda_impact", { precision: 15, scale: 2 }).notNull().default("0"),

  isRecurring: boolean("is_recurring").default(false),
  evidenceReference: text("evidence_reference"),
  notes: text("notes"),
  isIncluded: boolean("is_included").default(true),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 8i. Lender Income Shading Table (SPRINT-6B F009)
// Lender-specific income shading rules by income type and borrower type
export const lenderIncomeShading = pgTable("crm_x_lender_income_shading", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  lenderId: uuid("lender_id").references(() => lenders.id, { onDelete: "cascade" }).notNull(),

  incomeType: text("income_type").notNull(),
  borrowerType: text("borrower_type").default("all"),
  shadingPercentage: numeric("shading_percentage", { precision: 5, scale: 2 }).notNull(),
  minYearsEmployed: integer("min_years_employed"),
  requiresAbn: boolean("requires_abn").default(false),
  minAbnYears: integer("min_abn_years"),
  ruleDescription: text("rule_description"),
  isActive: boolean("is_active").default(true),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// 8j. Lender Health Scores Table (SPRINT-6B F010)
// Lender-focused credit assessment with component scores, health rating, and recommendation
export const lenderHealthScores = pgTable("crm_x_lender_health_scores", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),
  financialPeriodId: uuid("financial_period_id"),
  assessmentDate: date("assessment_date").notNull(),
  periodLabel: text("period_label"),

  // Component Scores (0-100)
  serviceabilityScore: integer("serviceability_score"),
  leverageScore: integer("leverage_score"),
  liquidityScore: integer("liquidity_score"),
  coverageScore: integer("coverage_score"),
  overallLenderScore: integer("overall_lender_score"),

  assessmentStatus: text("assessment_status").$type<AssessmentStatus>(),
  assessmentReasons: text("assessment_reasons").array().$type<AssessmentReason[]>(),
  calculationVersion: text("calculation_version"),
  basis: jsonb("basis").$type<FinancialBasis>(),

  // Rating & Trend
  healthRating: text("health_rating"), // investment_grade, acceptable, watchlist, substandard, doubtful
  trendDirection: text("trend_direction"), // improving, stable, declining

  // Key Metrics
  icrRatio: numeric("icr_ratio", { precision: 10, scale: 2 }),
  dscrRatio: numeric("dscr_ratio", { precision: 10, scale: 2 }),
  lvr: numeric("lvr", { precision: 5, scale: 2 }),

  // Concerns & Mitigators
  keyConcerns: text("key_concerns").array().default(sql`'{}'::text[]`),
  mitigatingFactors: text("mitigating_factors").array().default(sql`'{}'::text[]`),

  // Recommendation
  lenderRecommendation: text("lender_recommendation"), // approve, conditional_approve, refer, decline

  // Audit
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// 8k. Cash Flow Projections Table (SPRINT-6B F011)
// Deal-level cash flow modelling with multiple scenarios
export const faProjectionTypeEnum = pgEnum("fa_projection_type", ["monthly", "quarterly", "annual"]);
export const faScenarioTypeEnum = pgEnum("fa_scenario_type", ["base_case", "worst_case", "best_case", "stress_test"]);

export const cashFlowProjections = pgTable("crm_x_cash_flow_projections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }).notNull(),

  projectionName: varchar("projection_name", { length: 255 }).notNull(),
  projectionType: faProjectionTypeEnum("projection_type").notNull(),
  scenarioType: faScenarioTypeEnum("scenario_type").notNull().default("base_case"),
  startDate: date("start_date").notNull(),
  endDate: date("end_date").notNull(),
  periods: integer("periods"),

  sourceEntityId: uuid("source_entity_id"),
  sourcePeriodId: uuid("source_period_id"),

  projectionData: jsonb("projection_data").notNull(),
  totalInflows: numeric("total_inflows", { precision: 15, scale: 2 }),
  totalOutflows: numeric("total_outflows", { precision: 15, scale: 2 }),
  netCashFlow: numeric("net_cash_flow", { precision: 15, scale: 2 }),
  averageMonthlySurplus: numeric("average_monthly_surplus", { precision: 15, scale: 2 }),
  minimumMonthlyBalance: numeric("minimum_monthly_balance", { precision: 15, scale: 2 }),

  assumptions: jsonb("assumptions"),

  coversDebtService: integer("covers_debt_service"),
  debtServiceBuffer: numeric("debt_service_buffer", { precision: 15, scale: 2 }),

  notes: text("notes"),

  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  createdBy: uuid("created_by"),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

// Deal Serviceability Schemas (SPRINT-6B F001)
export const insertDealServiceabilitySchema = createInsertSchema(dealServiceability).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  // Also omit calculated/derived fields - these are server-computed
  rateUsed: true,
  serviceabilityRepaymentAnnual: true,
  serviceabilityRepaymentMonthly: true,
  totalAvailableIncome: true,
  totalExistingCommitments: true,
  proposedCommitment: true,
  totalCommitments: true,
  debtServiceCoverageRatio: true,
  interestCoverageRatio: true,
  passesDscr: true,
  passesIcr: true,
  passesServicing: true,
  calculatedAt: true,
  organisationId: true,
  sensitivityAnalysis: true,
  guarantorStatements: true,
  guarantorIncomeContribution: true,
  guarantorDebtContribution: true,
  incomeMethodology: true,
}).extend({
  // All rates stored as decimals (0.065 for 6.5%)
  customerRate: z.number().min(0).max(1),
  assessmentRate: z.number().min(0).max(1).nullable().optional(),
  bufferRate: z.number().min(0).max(1).nullable().optional(),
  loanAmount: z.number().positive(),
  loanTermMonths: z.number().int().positive(),
  lenderMinDscr: z.number().min(0).nullable().optional(),
  lenderMinIcr: z.number().min(0).nullable().optional(),
  // repaymentType accepts pi/io for API but stores as text in DB
  repaymentType: z.enum(["pi", "io", "principal_and_interest", "interest_only"]).nullable().optional(),
  incomeSource: z.string().nullable().optional(),
  serviceabilityApproach: z.enum(["customer_rate", "assessment_rate", "customer_rate_plus_buffer", "higher_of"]),
});
export const updateDealServiceabilitySchema = insertDealServiceabilitySchema.partial();
export const selectDealServiceabilitySchema = createSelectSchema(dealServiceability);

// Income Sources Schemas (SPRINT-6B F004)
export const insertDealIncomeSourcesSchema = createInsertSchema(dealIncomeSources, {
  includeBusinessIncome: z.boolean(),
  includeRentalIncome: z.boolean(),
  includePersonalIncome: z.boolean(),
  includeDividendIncome: z.boolean(),
  includeTrustIncome: z.boolean(),
  includeOtherIncome: z.boolean(),
  businessIncomeShading: z.number().min(0).max(100).nullable().optional(),
  rentalIncomeShading: z.number().min(0).max(100).nullable().optional(),
  personalIncomeShading: z.number().min(0).max(100).nullable().optional(),
  dividendIncomeShading: z.number().min(0).max(100).nullable().optional(),
  trustIncomeShading: z.number().min(0).max(100).nullable().optional(),
  totalGrossIncome: z.number().min(0).optional(),
  totalShadedIncome: z.number().min(0).optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateDealIncomeSourcesSchema = insertDealIncomeSourcesSchema.partial();
export const selectDealIncomeSourcesSchema = createSelectSchema(dealIncomeSources);

// Contact Income Schemas (SPRINT-6B F005)
export const insertDealContactIncomeSchema = createInsertSchema(dealContactIncome, {
  incomeType: z.enum(["payg_salary", "payg_wage", "self_employed", "rental", "dividend", "trust_distribution", "government_benefit", "superannuation", "investment", "other"]),
  frequency: z.enum(["weekly", "fortnightly", "monthly", "quarterly", "annually"]),
  verificationStatus: z.enum(["unverified", "pending", "verified", "rejected"]).optional().default("unverified"),
  grossAmount: z.number().positive(),
  annualGross: z.number().min(0).optional(),
  shadingPercentage: z.number().min(0).max(100).optional().default(100),
  shadedAnnual: z.number().min(0).optional(),
  includeInServicing: z.boolean().optional().default(true),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateDealContactIncomeSchema = insertDealContactIncomeSchema.partial();
export const selectDealContactIncomeSchema = createSelectSchema(dealContactIncome);

// Account Commitments Schemas (SPRINT-6B F006)
export const insertAccountCommitmentSchema = createInsertSchema(accountCommitments, {
  commitmentType: z.enum(["loan", "lease", "hire_purchase", "chattel_mortgage", "overdraft", "credit_card", "other"]),
  repaymentFrequency: z.enum(["weekly", "fortnightly", "monthly", "quarterly", "annually"]),
  currentBalance: z.number(),
  repaymentAmount: z.number(),
  annualRepayment: z.number().min(0).optional(),
  originalAmount: z.number().nullable().optional(),
  creditLimit: z.number().nullable().optional(),
  interestRate: z.number().nullable().optional(),
  includeInServicing: z.boolean().optional().default(true),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateAccountCommitmentSchema = insertAccountCommitmentSchema.partial();
export const selectAccountCommitmentSchema = createSelectSchema(accountCommitments);

// Living Expenses Schemas (SPRINT-6B F007)
export const insertLivingExpenseSchema = createInsertSchema(livingExpensesTable, {
  expenseCategory: z.enum(["housing", "utilities", "transport", "food_groceries", "insurance", "education_childcare", "telecommunications", "health_medical", "personal_lifestyle", "child_support_alimony", "other"]),
  frequency: z.enum(["weekly", "fortnightly", "monthly", "quarterly", "annually"]),
  amount: z.number(),
  annualAmount: z.number().min(0).optional(),
  isJointExpense: z.boolean().optional().default(false),
  hemApplicable: z.boolean().optional().default(true),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateLivingExpenseSchema = insertLivingExpenseSchema.partial();
export const selectLivingExpenseSchema = createSelectSchema(livingExpensesTable);

// ICR Calculations Schemas (SPRINT-6B F002)
export const insertIcrCalculationSchema = createInsertSchema(icrCalculations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  organisationId: true,
  sensitivityAnalysis: true,
}).extend({
  // Type refinements for numeric fields
  ebitda: z.number(),
  adjustedEbitda: z.number(),
  addbacks: z.number().nullable().optional(),
  totalIncomeForServicing: z.number(),
  existingInterest: z.number(),
  proposedInterest: z.number(),
  totalInterest: z.number(),
  icrRatio: z.number(),
  icrStatus: z.enum(["strong", "adequate", "marginal", "weak"]),
  lenderMinIcr: z.number().nullable().optional(),
  meetsRequirement: z.union([z.boolean(), z.number().int()]).transform(val => val === true || val === 1 ? 1 : val === false || val === 0 ? 0 : val).nullable().optional(),
  headroomPercentage: z.number().nullable().optional(),
  priorPeriodIcr: z.number().nullable().optional(),
  priorPeriod2Icr: z.number().nullable().optional(),
  priorPeriod3Icr: z.number().nullable().optional(),
  yoyChange: z.number().nullable().optional(),
  yoyChangePercentage: z.number().nullable().optional(),
  trendDirection: z.enum(["improving", "stable", "declining"]).nullable().optional(),
  trendPeriodsAnalysed: z.number().int().nullable().optional(),
  threeYearAvgIcr: z.number().nullable().optional(),
  threeYearMinIcr: z.number().nullable().optional(),
  threeYearMaxIcr: z.number().nullable().optional(),
});
export const updateIcrCalculationSchema = insertIcrCalculationSchema.partial().extend({
  id: z.string().uuid(),
});
export const selectIcrCalculationSchema = createSelectSchema(icrCalculations);

// DSR Calculations
export const insertDsrCalculationSchema = createInsertSchema(dsrCalculations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateDsrCalculationSchema = insertDsrCalculationSchema.partial().extend({
  id: z.string().uuid().optional(),
});

export const selectDsrCalculationSchema = createSelectSchema(dsrCalculations);

// Deal Adjustments Schemas (SPRINT-6B F008)
export const insertDealAdjustmentSchema = createInsertSchema(dealAdjustments, {
  adjustmentType: z.string().min(1),
  description: z.string().min(1),
  amount: z.number().positive(),
  adjustmentDirection: z.union([z.literal(1), z.literal(-1)]),
  ebitdaImpact: z.number().optional(),
  isRecurring: z.boolean().optional().default(false),
  evidenceReference: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  isIncluded: z.boolean().optional().default(true),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateDealAdjustmentSchema = insertDealAdjustmentSchema.partial();
export const selectDealAdjustmentSchema = createSelectSchema(dealAdjustments);

// Lender Income Shading Schemas (SPRINT-6B F009)
export const insertLenderIncomeShadingSchema = createInsertSchema(lenderIncomeShading, {
  incomeType: z.string().min(1),
  shadingPercentage: z.number().min(0).max(100),
  borrowerType: z.string().optional().default("all"),
  minYearsEmployed: z.number().int().nullable().optional(),
  requiresAbn: z.boolean().optional().default(false),
  minAbnYears: z.number().int().nullable().optional(),
  ruleDescription: z.string().nullable().optional(),
  isActive: z.boolean().optional().default(true),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const updateLenderIncomeShadingSchema = insertLenderIncomeShadingSchema.partial();
export const selectLenderIncomeShadingSchema = createSelectSchema(lenderIncomeShading);

// Lender Health Scores Schemas (SPRINT-6B F010)
export const insertLenderHealthScoreSchema = createInsertSchema(lenderHealthScores).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  serviceabilityScore: z.number().int().min(0).max(100).optional().default(0),
  leverageScore: z.number().int().min(0).max(100).optional().default(0),
  liquidityScore: z.number().int().min(0).max(100).optional().default(0),
  coverageScore: z.number().int().min(0).max(100).optional().default(0),
  overallLenderScore: z.number().int().min(0).max(100).optional().default(0),
  healthRating: z.enum(["investment_grade", "acceptable", "watchlist", "substandard", "doubtful"]).nullable().optional(),
  trendDirection: z.enum(["improving", "stable", "declining"]).nullable().optional(),
  icrRatio: z.number().nullable().optional(),
  dscrRatio: z.number().nullable().optional(),
  lvr: z.number().nullable().optional(),
  keyConcerns: z.array(z.string()).optional().default([]),
  mitigatingFactors: z.array(z.string()).optional().default([]),
  lenderRecommendation: z.enum(["approve", "conditional_approve", "refer", "decline"]).nullable().optional(),
});
export const updateLenderHealthScoreSchema = insertLenderHealthScoreSchema.partial();
export const selectLenderHealthScoreSchema = createSelectSchema(lenderHealthScores);

// Cash Flow Projections Schemas (SPRINT-6B F011)
export const insertCashFlowProjectionSchema = createInsertSchema(cashFlowProjections, {
  projectionName: z.string().min(1),
  projectionType: z.enum(["monthly", "quarterly", "annual"]),
  scenarioType: z.enum(["base_case", "worst_case", "best_case", "stress_test"]),
  startDate: z.string(),
  endDate: z.string(),
  projectionData: z.any(),
  assumptions: z.any().nullable().optional(),
  notes: z.string().nullable().optional(),
  coversDebtService: z.number().int().nullable().optional(),
  debtServiceBuffer: z.number().nullable().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  organisationId: true,
  totalInflows: true,
  totalOutflows: true,
  netCashFlow: true,
  averageMonthlySurplus: true,
  minimumMonthlyBalance: true,
  periods: true,
});
export const updateCashFlowProjectionSchema = insertCashFlowProjectionSchema.partial();
export const selectCashFlowProjectionSchema = createSelectSchema(cashFlowProjections);

// ============================================================================
// TYPES
// ============================================================================

// SPRINT-6B F001: Deal Serviceability Types
export type InsertDealServiceability = z.infer<typeof insertDealServiceabilitySchema>;
export type UpdateDealServiceability = z.infer<typeof updateDealServiceabilitySchema>;
export type DealServiceability = typeof dealServiceability.$inferSelect;

// SPRINT-6B F002: ICR Calculations Types
export type InsertIcrCalculation = z.infer<typeof insertIcrCalculationSchema>;
export type UpdateIcrCalculation = z.infer<typeof updateIcrCalculationSchema>;
export type IcrCalculation = typeof icrCalculations.$inferSelect;

// SPRINT-6B F003: DSR Calculations Types
export type InsertDsrCalculation = z.infer<typeof insertDsrCalculationSchema>;
export type UpdateDsrCalculation = z.infer<typeof updateDsrCalculationSchema>;
export type DsrCalculation = typeof dsrCalculations.$inferSelect;

// SPRINT-6B F006: Account Commitments Types
export type InsertAccountCommitment = z.infer<typeof insertAccountCommitmentSchema>;
export type UpdateAccountCommitment = z.infer<typeof updateAccountCommitmentSchema>;
export type AccountCommitment = typeof accountCommitments.$inferSelect;

// SPRINT-6B F007: Living Expenses Types
export type InsertLivingExpense = z.infer<typeof insertLivingExpenseSchema>;
export type UpdateLivingExpense = z.infer<typeof updateLivingExpenseSchema>;
export type LivingExpense = typeof livingExpensesTable.$inferSelect;

// SPRINT-6B F008: Deal Adjustments Types
export type InsertDealAdjustment = z.infer<typeof insertDealAdjustmentSchema>;
export type UpdateDealAdjustment = z.infer<typeof updateDealAdjustmentSchema>;
export type DealAdjustment = typeof dealAdjustments.$inferSelect;

// SPRINT-6B F009: Lender Income Shading Types
export type InsertLenderIncomeShading = z.infer<typeof insertLenderIncomeShadingSchema>;
export type UpdateLenderIncomeShading = z.infer<typeof updateLenderIncomeShadingSchema>;
export type LenderIncomeShading = typeof lenderIncomeShading.$inferSelect;

// SPRINT-6B F010: Lender Health Scores Types
export type InsertLenderHealthScore = z.infer<typeof insertLenderHealthScoreSchema>;
export type UpdateLenderHealthScore = z.infer<typeof updateLenderHealthScoreSchema>;
export type LenderHealthScore = typeof lenderHealthScores.$inferSelect;

// SPRINT-6B F011: Cash Flow Projections Types
export type InsertCashFlowProjection = z.infer<typeof insertCashFlowProjectionSchema>;
export type UpdateCashFlowProjection = z.infer<typeof updateCashFlowProjectionSchema>;
export type CashFlowProjection = typeof cashFlowProjections.$inferSelect;
